import { useRef, useState, useEffect } from 'react';
import classNames from 'classnames';

import Tooltip from 'components/partials/tooltip/tooltip';

interface UserInfo {
  fullName?: string;
  initials?: string;
  role?: string;
}

interface BaseAccountBlockProps {
  user: UserInfo | null;
  isCollapsed: boolean;
  isActive?: boolean;
  profilePath?: string;
  renderLink: (props: Record<string, unknown>) => React.ReactNode;
}

const BaseAccountBlock = ({
  user,
  isCollapsed,
  isActive = false,
  profilePath = '/profile',
  renderLink,
}: BaseAccountBlockProps) => {
  const textContainerRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const { fullName = 'User', initials = 'U', role = 'User' } = user || {};

  useEffect(() => {
    const el = textContainerRef.current;

    if (!el) return undefined;

    const checkTruncation = () => {
      const nameEl = el.querySelector('p');

      setIsTruncated(nameEl ? nameEl.scrollWidth > nameEl.clientWidth : false);
    };

    const observer = new ResizeObserver(checkTruncation);

    observer.observe(el);
    checkTruncation();

    return () => observer.disconnect();
  }, [fullName, role]);

  if (isCollapsed) {
    const collapsedContent = (
      <span
        className={classNames(
          'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors',
          {
            'bg-blue-700 text-white': isActive,
            'bg-lime-200 text-lime-900 hover:bg-lime-500': !isActive,
          }
        )}
      >
        {initials}
      </span>
    );

    return (
      <div className="border-t border-navy-200 h-[84px] px-2 flex items-center justify-center">
        {renderLink({
          to: profilePath,
          className: 'flex items-center justify-center',
          title: fullName,
          'aria-label': `Profile: ${fullName}`,
          children: collapsedContent,
        })}
      </div>
    );
  }

  const expandedContent = (
    <>
      <div
        className="w-10 h-10 rounded-full bg-lime-200 text-lime-900 flex items-center justify-center text-sm font-semibold flex-shrink-0"
        data-testid="side-menu-account-initials"
      >
        {initials}
      </div>
      <div ref={textContainerRef} className="min-w-0 flex-1 overflow-hidden">
        <Tooltip
          content={`${fullName} — ${role}`}
          position="top"
          enabled={isTruncated}
        >
          <p
            className="text-sm font-semibold text-gray-700 truncate"
            data-testid="side-menu-account-full-name"
          >
            {fullName}
          </p>
          <p
            className="text-sm leading-5 text-gray-500 truncate"
            data-testid="side-menu-account-role"
          >
            {role}
          </p>
        </Tooltip>
      </div>
    </>
  );

  return (
    <div className="border-t border-navy-200 h-[84px] px-3 flex items-center overflow-hidden">
      {renderLink({
        to: profilePath,
        className: classNames(
          'flex items-center gap-3 px-2 py-2 rounded-lg transition-colors w-full min-w-0',
          {
            'bg-blue-50': isActive,
            'hover:bg-navy-50': !isActive,
          }
        ),
        children: expandedContent,
        'data-testid': 'side-menu-account-block',
      })}
    </div>
  );
};

export default BaseAccountBlock;
